import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '../lib/api-client';
import { queryKeys } from '../lib/react-query';
import { toast } from 'sonner';

// ============ Interaction Hooks ============

export function useReceivedInterests(filters?: { type?: string; status?: string }) {
  return useQuery({
    queryKey: ['interactions', 'received', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.status) params.append('status', filters.status);

      const response = await axiosInstance.get(`/interactions/received?${params.toString()}`, {
        timeout: 3000
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 1, // 1 minute
    enabled: !!localStorage.getItem('token'),
  });
}

export function useSentInterests(filters?: { type?: string }) {
  return useQuery({
    queryKey: ['interactions', 'sent', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);

      const response = await axiosInstance.get(`/interactions/sent?${params.toString()}`, {
        timeout: 3000
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 1, // 1 minute
    enabled: !!localStorage.getItem('token'),

  });
}

export function useAcceptedInterests() {
  return useQuery({
    queryKey: ['interactions', 'received', { status: 'accepted' }],
    queryFn: async () => {
      const response = await axiosInstance.get('/interactions/received?status=accepted', {
        timeout: 3000
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!localStorage.getItem('token'),
  });
}

export function useDeclinedInterests() {
  return useQuery({
    queryKey: ['interactions', 'received', { status: 'declined' }],
    queryFn: async () => {
      const response = await axiosInstance.get('/interactions/received?status=declined', {
        timeout: 3000
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!localStorage.getItem('token'),
  });
}

export function useSendInteraction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ profileId, type, message }: { profileId: string; type: string; message?: string }) => {
      const response = await axiosInstance.post('/interactions', {
        profileId,
        type,
        message
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactions'] });
      toast.success('Interest sent successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to send interest';
      toast.error(errorMessage);
    },
  });
}

export function useAcceptInterest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (interestId: string) => {
      const response = await axiosInstance.post(`/interactions/accept/${interestId}`, {}, {
        timeout: 3000
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactions'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.chats.list });
      toast.success('Interest accepted! You can now chat.');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to accept interest';
      toast.error(errorMessage);
    },
  });
}

export function useDeclineInterest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (interestId: string) => {
      const response = await axiosInstance.post(`/interactions/decline/${interestId}`, {}, {
        timeout: 3000
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactions'] });
      toast.success('Interest declined');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to decline interest';
      toast.error(errorMessage);
    },
  });
}

// ============ Activity Analytics Hooks ============

export function useProfileAnalytics() {
  return useQuery({
    queryKey: ['profile-analytics', 'stats'],
    queryFn: async () => {
      const response = await axiosInstance.get('/profile-analytics/stats', {
        timeout: 3000
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!localStorage.getItem('token'),
  });
}


export function useActivityStats() {
  return useQuery({
    queryKey: ['activities', 'stats'],
    queryFn: async () => {
      const response = await axiosInstance.get('/activities/stats', {
        timeout: 3000
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: !!localStorage.getItem('token'),
  });
}

export function useProfileVisits(limit: number = 20) {
  return useQuery({
    queryKey: ['activities', 'visits', limit],
    queryFn: async () => {
      const response = await axiosInstance.get(`/activities/views?type=profile_visit&limit=${limit}`, {
        timeout: 3000
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!localStorage.getItem('token'),
  });
}

export function useContactViews(limit: number = 20) {
  return useQuery({
    queryKey: ['activities', 'contact-views', limit],
    queryFn: async () => {
      const response = await axiosInstance.get(`/activities/views?type=contact_view&limit=${limit}`, {
        timeout: 3000
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!localStorage.getItem('token'),
  });
}

export function useShortlistedProfiles(limit: number = 20) {
  return useQuery({
    queryKey: ['interactions', 'shortlisted', limit],
    queryFn: async () => {
      const response = await axiosInstance.get(`/interactions/shortlisted?limit=${limit}`, {
        timeout: 3000
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!localStorage.getItem('token'),
  });
}

export function useRemoveShortlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileId: string) => {
      const response = await axiosInstance.post('/interactions', {
        profileId,
        type: 'shortlist'
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactions', 'shortlisted'] });
      queryClient.invalidateQueries({ queryKey: ['activities', 'stats'] });
      toast.success('Removed from shortlist');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to remove from shortlist';
      toast.error(errorMessage);
    },
  });
}

// ============ Report User Hook ============

export function useReportUser() {
  return useMutation({
    mutationFn: async ({ userId, reason, description }: { userId: string; reason: string; description?: string }) => {
      const response = await axiosInstance.post('/users/report', {
        reportedUserId: userId,
        reason,
        description
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('User reported successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to report user';
      toast.error(errorMessage);
    },
  });
}

// ============ Block/Unblock User Hooks ============

export function useCheckBlockStatus(userId: string) {
  return useQuery({
    queryKey: ['block-status', userId],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(`/users/is-blocked/${userId}`);
        return response.data;
      } catch (error: any) {
        // If endpoint doesn't exist (404), return default data
        if (error?.response?.status === 404) {
          return { success: true, data: { isBlockedByThem: false } };
        }
        throw error;
      }
    },
    enabled: !!userId && !!localStorage.getItem('token'),
    retry: false, // Don't retry on 404
    staleTime: 30000, // Cache for 30 seconds
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      const response = await axiosInstance.delete(`/conversations/${conversationId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.chats.list });
      toast.success('Conversation deleted');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete conversation';
      toast.error(errorMessage);
    },
  });
}